import { useUpdateSearchParam } from '@/Utility/hooks';
import SearchIcon from '@mui/icons-material/Search';
import InputBase, { type InputBaseProps } from '@mui/material/InputBase';
import { alpha, styled } from '@mui/material/styles';
import { useSearch } from '@tanstack/react-router';
import { debounce } from 'lodash';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import './SearchBar.less';

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 1),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.75),
  },
  marginLeft: 0,
  width: '100%',
  boxShadow: theme.shadows[1],
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  width: '90%',
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
  },
}));

interface Props {
  style?: React.CSSProperties;
  disabled?: boolean;
}

const SearchBar = (props: Props) => {
  const [inputValue, setInputValue] = useState<string>('');
  const inputValueRef = useRef<string>(inputValue);

  const isUpdatingRef = useRef<boolean>(false);
  const updateSearchParam = useUpdateSearchParam();

  const searchParamValue = useSearch({
    strict: false,
    select(state) {
      return state?.search;
    },
  });

  useEffect(() => {
    if (isUpdatingRef.current) return;

    if (searchParamValue && searchParamValue !== inputValue) {
      setInputValue(searchParamValue);
      inputValueRef.current = searchParamValue;
    }
  }, [searchParamValue]);

  const setSearch = () => {
    updateSearchParam('search', inputValueRef.current);
    isUpdatingRef.current = false;
  };

  const setSearchdebounced = useCallback(debounce(setSearch, 750, { leading: false, trailing: true }), []);

  const setSearchWrapper = useCallback((value?: string) => {
    setInputValue(value ?? '');
    inputValueRef.current = value ?? '';
    isUpdatingRef.current = true;

    setSearchdebounced();
  }, []);

  return (
    <Search className="SearchBar z-search-bar" {...props}>
      <SearchIconWrapper>
        <SearchIcon />
      </SearchIconWrapper>
      <StyledInputBase
        onChange={({ target: { value } }) => {
          setSearchWrapper(value);
        }}
        value={inputValue}
        placeholder="Search…"
        inputProps={{ 'aria-label': 'search' }}
        className="SearchInput z-search-bar-input"
      />
    </Search>
  );
};

type BaseSearchComponentProps = {
  inputProps: InputBaseProps;
} & Props;

export const BaseSearchComponent = ({ inputProps, ...props }: BaseSearchComponentProps) => {
  return (
    <Search className="SearchBar z-search-bar" {...props}>
      <SearchIconWrapper>
        <SearchIcon />
      </SearchIconWrapper>
      <StyledInputBase
        placeholder="Search…"
        inputProps={{ 'aria-label': 'search' }}
        className="SearchInput z-search-bar-input"
        aria-label="search"
        {...inputProps}
      />
    </Search>
  );
};

export default SearchBar;
